import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CommentIcon from '@mui/icons-material/Comment';
import CommentsDisabledIcon from '@mui/icons-material/CommentsDisabled';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MovieIcon from '@mui/icons-material/Movie';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import SubtitlesOffIcon from '@mui/icons-material/SubtitlesOff';
import ViewsIcon from '@mui/icons-material/ViewList';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import {
    Badge,
    Box,
    Button,
    Checkbox,
    ClickAwayListener,
    IconButton,
    Paper,
    Popper,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { toJS } from 'mobx';
import React, { useContext, useEffect, useState } from 'react';
import SentimentIcon from '../../icons/sentimentIcon';
import SpeakerIcon from '../../icons/speakerIcon';
import { filterContext } from "../../stores/filters/filterContext";
import { messageContext } from "../../stores/messages/messageContext";
import { userContext } from '../../stores/users/userContext';
import { GIF_MAX_DURATION, viewItems } from '../constants';
import CustomModal from './customModal';
import './draggableToolbar.css';
const iconStyle = {
    fontSize: 18,
};


const DraggableToolbar = ({
    viewConfig,
    onPositionChange,
    rowCount = 0,
    selectedCount = 0,
    totalCount = 0,
    sentiments = {},
    onSentimentChange,
    speakers = {},
    onSpeakerChange,
    textLen = [0, 100],
    onLengthChange,
    seconds = [0, 100],
    onSecondsChange,
    onResetFilters,
    subtitlesEnabled,
    onSetResetSubtitles,
    spacesOnOff,
    onSetSpaces,
    onSearchChange,
    searchValue,
    store,
    currentView = 'Create a Video',
    onViewChange,
    makeMidia,
    precision,
    onPrecisionChange,
    videoWidth,
    onVideoWidthChange,
    onJumpToItem = null,
    setCloseSearchModalRef = null,
    videoFileExtension = 'mp4',
}) => {
    const x = toJS(sentiments);
    const [position, setPosition] = useState('top');
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    // Responsive video width ranges based on screen size
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const getVideoWidthRange = () => {
        if (isMobile) {
            return { min: 70, max: 130 }; // Small screens: 70-130
        } else if (isTablet) {
            return { min: 20, max: 250 }; // Medium screens: 20-250
        } else {
            return { min: 30, max: 400 }; // Large screens: 30-400
        }
    };

    const videoWidthRange = getVideoWidthRange();

    // Get screen-appropriate default value for reset button
    const getResponsiveDefault = () => {
        if (isMobile) {
            return 100; // Mobile default: mid-range of 70-130
        } else if (isTablet) {
            return 120; // Tablet default: good balance for tablets
        } else {
            return 200; // Desktop default: good balance for large screens
        }
    };

    const responsiveDefault = getResponsiveDefault();
    const [activeModal, setActiveModal] = useState(null);
    const [secondsValues, setSecondsValues] = useState(seconds);
    const [selectedCreateOptions, setSelectedCreateOptions] = useState([]);

    const userStore = useContext(userContext);
    const messageStore = useContext(messageContext);
    const filterStore = useContext(filterContext);
    const durationInMs = userStore.getDurationInMs()
    const currentDuration = (durationInMs !== undefined && durationInMs > 0 && durationInMs <= GIF_MAX_DURATION ? true : false)


    // Get current search text to determine if Super Cut should be enabled
    const currentSearchText = store.getSearch(currentView);
    const hasSuperCutEnabled = currentSearchText && currentSearchText.trim() !== '';

    const createOptions = [
        [`Create a ${videoFileExtension.toUpperCase()} video`, filterStore.hasSelectedSentences(currentView)],
        ['Super Cut (searched words only)', hasSuperCutEnabled && filterStore.hasSelectedSentences(currentView)],
        ['Create a MP3 audio', filterStore.hasSelectedSentences(currentView)],
        ['Create a GIF', currentDuration],
        ['Create a SRT file', filterStore.hasSelectedSentences(currentView)],
        ['Create a VTT file', filterStore.hasSelectedSentences(currentView)],
    ];
    // only show the create zip file with all SRT's option if the current view is Create a Video
    if (currentView === 'Create a Video') {
        createOptions.push(['Create zip file with all SRT\'s', true])
    }

    // Update secondsValues only when seconds prop changes from parent
    useEffect(() => {
        // Only update if the new seconds values are different from the current secondsValues
        if (seconds[0] !== secondsValues[0] || seconds[1] !== secondsValues[1]) {
            setSecondsValues(seconds);
        }
    }, [seconds]);

    // Set the close search modal reference
    useEffect(() => {
        if (setCloseSearchModalRef) {
            setCloseSearchModalRef(() => () => {
                setActiveModal(null);
            });
        }
    }, [setCloseSearchModalRef]);

    // Notify parent component when position changes
    useEffect(() => {
        if (onPositionChange) {
            onPositionChange(position);
        }
    }, [position, onPositionChange]);

    const handleSecondsChange = (newValues) => {
        setSecondsValues(newValues);
        onSecondsChange(newValues);
    };

    const handleButtonClick = (callback) => {
        setActiveModal(null);  // Close any open modal
        callback();  // Execute the original callback
    };

    const handleResetClick = () => {
        onResetFilters();
        setActiveModal(null);  // Close the modal
    };

    // Handle click on the toolbar handle to change position
    // If a direction is provided, set it explicitly; otherwise, cycle through positions.
    const handlePositionChange = (direction) => {
        if (direction) {
            setPosition(direction);
        } else {
            // Cycle through positions: left -> top -> right -> bottom -> left
            let newPosition;
            switch (position) {
                case 'left':
                    newPosition = 'top';
                    break;
                case 'top':
                    newPosition = 'right';
                    break;
                case 'right':
                    newPosition = 'bottom';
                    break;
                case 'bottom':
                    newPosition = 'left';
                    break;
                default:
                    newPosition = 'left';
            }
            setPosition(newPosition);
        }
    };

    const isVertical = position === 'left' || position === 'right';

    const sentimentLabels = {
        positive: 'Positive sentiment',
        negative: 'Negative sentiment',
        neutral: 'Neutral sentiment'
    };

    const handleCreateOptionChange = (option, checked) => {
        // Check if the option is enabled before allowing changes
        const optionConfig = createOptions.find(item =>
            (Array.isArray(item) && item[0] === option) || item === option
        );

        const isEnabled = Array.isArray(optionConfig) ? optionConfig[1] : true;

        if (!isEnabled) {
            return; // Don't allow changes to disabled options
        }

        // Update selected options
        setSelectedCreateOptions(prev =>
            checked
                ? [...prev, option]
                : prev.filter(item => item !== option)
        );
    };

    const handleViewChange = (event) => {
        const customEvent = new CustomEvent('viewChange', {
            detail: { view: event.target.value }
        });
        window.dispatchEvent(customEvent);
        if (onViewChange) {
            onViewChange(event.target.value);
        }
        // Only close the modal if it's not the Search modal
        if (activeModal !== 'Search') {
            setActiveModal(null);
        }
    };
    const handleCreateMedia = () => {
        setActiveModal('Create')
    }

    const handleSearchClick = () => {
        // Toggle the search modal - close it if it's already open, open it if it's closed
        setActiveModal(activeModal === 'Search' ? null : 'Search');
    }

    return (
        <Box
            className={`toolbar ${position} ${isDarkMode ? 'dark-theme' : 'light-theme'}`}
            sx={{
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                borderColor: theme.palette.divider,
                padding: 0,
                margin: 0,
                boxSizing: 'border-box'
            }}
        >
            {viewConfig?.handle && (
                <Box
                    className="toolbar-handle"
                    onClick={() => handleButtonClick(handlePositionChange)}
                    sx={{
                        backgroundColor: theme.palette.action.selected,
                        cursor: 'pointer',
                        padding: '8px',
                        margin: 0,
                        boxSizing: 'border-box'
                    }}
                >
                    <DragIndicatorIcon sx={{ color: theme.palette.text.secondary }} />
                </Box>
            )}
            <Box
                className="toolbar-content"
                sx={{
                    margin: 0,
                    overflow: 'auto',
                    position: 'relative', // Ensure proper stacking context
                    // Scrollable container with proper scrollbars
                    '&::-webkit-scrollbar': {
                        width: isVertical ? '6px' : 'auto',
                        height: isVertical ? 'auto' : '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.divider,
                        borderRadius: '3px',
                        '&:hover': {
                            backgroundColor: theme.palette.text.secondary,
                        }
                    },
                    // For Firefox
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${theme.palette.divider} transparent`,
                    // Ensure dropdowns can escape the container
                    '& .MuiPopper-root': {
                        zIndex: 1400
                    }
                }}
            >
                <Stack
                    direction={isVertical ? "column" : "row"}
                    spacing={2}
                    alignItems="center"
                    sx={{
                        width: '100%',
                        minWidth: isVertical ? 'auto' : 'max-content', // Prevent wrapping in horizontal mode
                        minHeight: isVertical ? 'max-content' : 'auto', // Prevent wrapping in vertical mode
                        '& > *': {
                            minWidth: isVertical ? '100%' : 'auto',
                            flexShrink: 0, // Prevent items from shrinking
                        }
                    }}
                >
                    {/* Position Change Arrows Section */}
                    <Box sx={{ minWidth: isVertical ? '100%' : 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="caption" sx={{
                            color: theme.palette.text.secondary,
                            display: 'block',
                            mb: 0.7,
                            textAlign: 'left',
                        }}>
                            Position
                        </Typography>
                        <Stack
                            direction={isVertical ? "column" : "row"}
                            spacing={1}
                            alignItems="flex-start"
                            justifyContent="flex-start"
                            sx={{
                                '& .MuiIconButton-root': {
                                    padding: '4px',
                                }
                            }}
                        >
                            {position !== 'left' && viewConfig?.arrowLeft && <Tooltip title="Go to Left">
                                <IconButton onClick={() => handlePositionChange('left')} size="small">
                                    <KeyboardArrowLeftIcon />
                                </IconButton>
                            </Tooltip>}
                            {position !== 'top' && viewConfig?.arrowUp && <Tooltip title="Go to Top">
                                <IconButton onClick={() => handlePositionChange('top')} size="small">
                                    <KeyboardArrowUpIcon />
                                </IconButton>
                            </Tooltip>}
                            {position !== 'right' && viewConfig?.arrowRight && <Tooltip title="Go to Right">
                                <IconButton onClick={() => handlePositionChange('right')} size="small">
                                    <KeyboardArrowRightIcon />
                                </IconButton>
                            </Tooltip>}
                            {position !== 'bottom' && viewConfig?.arrowDown && <Tooltip title="Go to Bottom">
                                <IconButton onClick={() => handlePositionChange('bottom')} size="small">
                                    <KeyboardArrowDownIcon />
                                </IconButton>
                            </Tooltip>}
                        </Stack>
                    </Box>
                    {/* Stats Section */}
                    {viewConfig?.stats && (
                        <Box sx={{ minWidth: isVertical ? '100%' : 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Typography variant="caption" sx={{
                                color: theme.palette.text.secondary,
                                display: 'block',
                                mb: 0.7,
                                textAlign: 'left',
                            }}>
                                Rows
                            </Typography>
                            <Stack direction={isVertical ? "column" : "row"} spacing={1} alignItems="flex-start" justifyContent="flex-start">
                                <Tooltip
                                    title={`Filtered Rows: ${rowCount} of ${totalCount}`}
                                    placement={position === 'left' ? 'right' : position === 'right' ? 'left' : 'bottom'}
                                >
                                    <Badge
                                        badgeContent={rowCount}
                                        max={9999}
                                        color="default"
                                        sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem' } }}
                                    >
                                        <FilterListIcon sx={{ color: "gray" }} />
                                    </Badge>
                                </Tooltip>
                                <Tooltip
                                    title={`Selected Rows: ${selectedCount}`}
                                    placement={position === 'left' ? 'right' : position === 'right' ? 'left' : 'bottom'}
                                >
                                    <Badge
                                        badgeContent={selectedCount === 0 ? '0' : selectedCount}
                                        max={9999}
                                        color="default"
                                        sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem' } }}
                                    >
                                        <CheckCircleIcon sx={{ color: "gray" }} />
                                    </Badge>
                                </Tooltip>
                            </Stack>
                        </Box>
                    )}

                    {/* Sentiment Section */}
                    {viewConfig?.sentiment && (
                        <Box sx={{ minWidth: isVertical ? '100%' : 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Typography variant="caption" sx={{
                                color: theme.palette.text.secondary,
                                display: 'block',
                                mb: 0,
                                textAlign: 'left'
                            }}>
                                Sentiment
                            </Typography>
                            <Stack
                                direction={isVertical ? "column" : "row"}
                                spacing={1}
                                alignItems="flex-start"
                                justifyContent="flex-start"
                                sx={{
                                    '& .MuiCheckbox-root': {
                                        padding: '4px',
                                        margin: isVertical ? '0' : '0',
                                    }
                                }}
                            >
                                {['positive', 'neutral', 'negative'].map((key) => (
                                    <Tooltip
                                        key={key}
                                        title={sentimentLabels[key]}
                                        placement={position === 'left' ? 'right' : position === 'right' ? 'left' : 'bottom'}
                                    >
                                        <Checkbox
                                            icon={<SentimentIcon type={key} />}
                                            checkedIcon={<SentimentIcon type={key} />}
                                            checked={sentiments[key]}
                                            value={key}
                                            onChange={(e) => handleButtonClick(() => onSentimentChange(e))}
                                            sx={{
                                                '&.Mui-checked': {
                                                    backgroundColor: theme.palette.action.selected,
                                                    borderRadius: '4px'
                                                }
                                            }}
                                        />
                                    </Tooltip>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Speaker Section - Inline Checkboxes */}
                    {viewConfig?.speaker && (
                        <Box sx={{ minWidth: isVertical ? '100%' : 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Typography variant="caption" sx={{
                                color: theme.palette.text.secondary,
                                display: 'block',
                                mb: 0,
                                textAlign: 'left'
                            }}>
                                Speaker
                            </Typography>
                            <Stack
                                direction={isVertical ? "column" : "row"}
                                spacing={1}
                                alignItems="flex-start"
                                justifyContent="flex-start"
                                sx={{
                                    '& .MuiCheckbox-root': {
                                        padding: '4px',
                                        margin: isVertical ? '0' : '0',
                                    }
                                }}
                            >
                                {Object.keys(speakers).map((speaker) => (
                                    <Tooltip
                                        key={speaker}
                                        title={`Speaker ${speaker}`}
                                        placement={position === 'left' ? 'right' : position === 'right' ? 'left' : 'bottom'}
                                    >
                                        <Checkbox
                                            icon={<SpeakerIcon speaker={speaker} />}
                                            checkedIcon={<SpeakerIcon speaker={speaker} />}
                                            checked={speakers[speaker]}
                                            value={speaker}
                                            onChange={(e) => handleButtonClick(() => onSpeakerChange(e))}
                                            sx={{
                                                '&.Mui-checked': {
                                                    backgroundColor: theme.palette.action.selected,
                                                    borderRadius: '4px'
                                                }
                                            }}
                                        />
                                    </Tooltip>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Length Input */}
                    {viewConfig?.length && (
                        <Box sx={{ minWidth: isVertical ? '100%' : 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Typography variant="caption" sx={{
                                color: theme.palette.text.secondary,
                                display: 'block',
                                mb: -0.7,
                                textAlign: 'left'
                            }}>
                                Length
                            </Typography>
                            <Stack direction={isVertical ? "column" : "row"} spacing={1} alignItems="center" justifyContent="left">
                                <Tooltip
                                    title={`Length ${textLen[0]} - ${textLen[1]}`}
                                    placement={position === 'left' ? 'right' : position === 'right' ? 'left' : 'bottom'}
                                >
                                    <IconButton
                                        onClick={() => setActiveModal('Length')}
                                        sx={{
                                            color: theme.palette.success.main,
                                            padding: '4px'
                                        }}
                                    >
                                        <ArrowForwardIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Box>
                    )}

                    {/* Seconds Input */}
                    {viewConfig?.seconds && (
                        <Box sx={{ minWidth: isVertical ? '100%' : 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Typography variant="caption" sx={{
                                color: theme.palette.text.secondary,
                                display: 'block',
                                mb: -0.7,
                                textAlign: 'left'
                            }}>
                                Time
                            </Typography>
                            <Stack direction={isVertical ? "column" : "row"} spacing={1} alignItems="center" justifyContent="flex-start">
                                <Tooltip
                                    title={`Time ${secondsValues[0]} - ${secondsValues[1]}`}
                                    placement={position === 'left' ? 'right' : position === 'right' ? 'left' : 'bottom'}
                                >
                                    <IconButton
                                        onClick={() => setActiveModal('Seconds')}
                                        sx={{
                                            color: theme.palette.warning.main,
                                            padding: '4px'
                                        }}
                                    >
                                        <WatchLaterIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Box>
                    )}

                    {/* Reset Section */}
                    {viewConfig?.resetFilters && (
                        <Box sx={{ minWidth: isVertical ? '100%' : 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Typography variant="caption" sx={{
                                color: theme.palette.text.secondary,
                                display: 'block',
                                mb: -0.7,
                                textAlign: 'left'
                            }}>
                                Reset
                            </Typography>
                            <Stack direction={isVertical ? "column" : "row"} spacing={1} alignItems="flex-start" justifyContent="flex-start">
                                <Tooltip
                                    title="Reset all filters"
                                    placement={position === 'left' ? 'right' : position === 'right' ? 'left' : 'bottom'}
                                >
                                    <IconButton
                                        onClick={handleResetClick}
                                        sx={{
                                            color: theme.palette.error.main,
                                            padding: '4px'
                                        }}
                                    >
                                        <RestartAltIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Box>
                    )}

                    {/* Options Section */}
                    <Box sx={{ minWidth: isVertical ? '100%' : 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="caption" sx={{
                            color: theme.palette.text.secondary,
                            display: 'block',
                            mb: -0.7,
                            textAlign: 'left'
                        }}>
                            Options
                        </Typography>
                        <Stack direction={isVertical ? "column" : "row"} spacing={1} alignItems="flex-start" justifyContent="flex-start">
                            {viewConfig?.subtitles && (
                                <Tooltip
                                    title={subtitlesEnabled ? "Subtitle on" : "Subtitle off"}
                                    placement={position === 'left' ? 'right' : position === 'right' ? 'left' : 'bottom'}
                                >
                                    <IconButton
                                        onClick={onSetResetSubtitles}
                                    >
                                        {(subtitlesEnabled ?
                                            <SubtitlesIcon color="action" sx={iconStyle} /> :
                                            <SubtitlesOffIcon color="action" sx={iconStyle} />)}
                                    </IconButton>
                                </Tooltip>
                            )}
                            {viewConfig?.spaces && (
                                <Tooltip
                                    title={spacesOnOff ? "Hide spaces" : "Show spaces"}
                                    placement={position === 'left' ? 'right' : position === 'right' ? 'left' : 'bottom'}
                                >
                                    <IconButton
                                        onClick={() => handleButtonClick(onSetSpaces)}
                                        sx={{
                                            color: spacesOnOff ? theme.palette.success.main : theme.palette.error.main,
                                            padding: '4px'
                                        }}
                                    >
                                        {spacesOnOff ? <CommentIcon /> : <CommentsDisabledIcon />}
                                    </IconButton>
                                </Tooltip>
                            )}

                            {viewConfig?.precision && (
                                <Tooltip
                                    title={precision ? "Seconds On" : "Seconds Off"}
                                    placement={position === 'left' ? 'right' : position === 'right' ? 'left' : 'bottom'}
                                >
                                    <IconButton
                                        onClick={onPrecisionChange}
                                        sx={{
                                            color: precision ? theme.palette.success.main : theme.palette.error.main,
                                            padding: '4px'
                                        }}
                                    >
                                        <AccessTimeIcon sx={iconStyle} />
                                    </IconButton>
                                </Tooltip>
                            )}

                            {viewConfig?.createVideo && (
                                <Tooltip
                                    title="Create Media"
                                    placement={position === 'left' ? 'right' : position === 'right' ? 'left' : 'bottom'}
                                >
                                    <IconButton
                                        onClick={handleCreateMedia}
                                        sx={{
                                            color: theme.palette.secondary.main,
                                            padding: '4px'
                                        }}
                                    >
                                        <MovieIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {viewConfig?.views && (
                                <Tooltip
                                    title={"Change View"}
                                    placement={position === 'left' ? 'right' : position === 'right' ? 'left' : 'bottom'}
                                >
                                    <IconButton
                                        onClick={() => setActiveModal('Views')}
                                        sx={{
                                            color: theme.palette.info.main,
                                            padding: '4px'
                                        }}
                                    >
                                        <ViewsIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {viewConfig?.videoWidth && (
                                <Tooltip
                                    title={`Video Width: ${videoWidth}`}
                                    placement={position === 'left' ? 'right' : position === 'right' ? 'left' : 'bottom'}
                                >
                                    <IconButton
                                        onClick={() => setActiveModal('VideoWidth')}
                                        sx={{
                                            color: theme.palette.primary.main,
                                            padding: '4px'
                                        }}
                                    >
                                        <AspectRatioIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                    </Box>

                    {/* Search Section */}
                    {viewConfig?.search && (
                        <Box sx={{ minWidth: isVertical ? '100%' : 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Typography variant="caption" sx={{
                                color: theme.palette.text.secondary,
                                display: 'block',
                                mb: -0.7,
                                textAlign: 'left'
                            }}>
                                Search
                            </Typography>
                            <Stack direction={isVertical ? "column" : "row"} spacing={1} alignItems="flex-start" justifyContent="flex-start">
                                <Tooltip
                                    title={activeModal === 'Search' ?
                                        "Close search" :
                                        (store.getSearch(currentView) !== "" ? "Searching for: " + store.getSearch(currentView) : "Search")}
                                    placement={position === 'left' ? 'right' : position === 'right' ? 'left' : 'bottom'}
                                >
                                    <IconButton
                                        onClick={handleSearchClick}
                                        sx={{
                                            color: theme.palette.info.main,
                                            padding: '4px'
                                        }}
                                    >
                                        <SearchIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </Box >
            <CustomModal
                open={activeModal === 'Length'}
                onClose={() => setActiveModal(null)}
                type="slider"
                value={textLen}
                onChange={(_, newValues) => onLengthChange(newValues)}
                min={textLen[0]}
                max={textLen[1]}
                color={theme.palette.success.main}
                title="Length Range"
            />
            <CustomModal
                open={activeModal === 'Seconds'}
                onClose={() => setActiveModal(null)}
                type="slider"
                value={secondsValues}
                onChange={(_, newValues) => handleSecondsChange(newValues)}
                min={seconds[0]}
                max={seconds[1]}
                color={theme.palette.warning.main}
                title={precision ? "Seconds and centiseconds" : "Seconds Range"}
                precision={precision}
            />
            <CustomModal
                open={activeModal === 'Search'}
                onClose={() => setActiveModal(null)}
                type="search"
                color={theme.palette.info.main}
                title="Search"
                onSearchChange={onSearchChange}
                store={store}
                searchValue={store.getSearch(currentView)}
                currentView={currentView}
            />
            <CustomModal
                open={activeModal === 'Views'}
                onClose={() => setActiveModal(null)}
                type="views"
                color={theme.palette.info.main}
                title="Select View"
                value={currentView}
                onChange={handleViewChange}
                options={viewItems}
            />
            <CustomModal
                open={activeModal === 'Create'}
                onClose={() => {
                    setActiveModal(null);
                    setSelectedCreateOptions([]);
                }}
                type="create"
                color={theme.palette.secondary.main}
                title="Create Media"
                options={createOptions}
                selectedOptions={selectedCreateOptions}
                onOptionChange={handleCreateOptionChange}
                onCreateMedia={makeMidia}
                searchValue={store.getSearch(currentView)}
            />
            <CustomModal
                open={activeModal === 'VideoWidth'}
                onClose={() => setActiveModal(null)}
                type="slider"
                value={videoWidth}
                onChange={(_, newValue) => onVideoWidthChange(newValue)}
                min={videoWidthRange.min}
                max={videoWidthRange.max}
                color={theme.palette.primary.main}
                title="Video Width"
                step={10}
                unit=""
                isSingleValue={true}
            />

        </Box >
    );
};

export default DraggableToolbar; 